'use client';

import { c as _c } from "react/compiler-runtime";
import { BasicTooltip } from '@/comps/basic-tooltip';
import { chartIndexCrosshairPlugin } from '@/domains/pages/shared/chart-crosshair';
import { TIME_SERIES_INTERACTION } from '@/domains/pages/shared/chart-interaction';
import { positionChartExternalTooltip } from '@/domains/pages/shared/chart-external-tooltip';
import { buildLevelMixTooltipHtml } from '@/domains/pages/ai-proficiency/components/chart-tooltip';
import { AIFI_LINE_CHART_OPTIONS, AIFI_LEVEL_CHART_COLUMN_SPAN, AIFI_LEVEL_COLORS, AIFI_LEVEL_DESCRIPTIONS, AIFI_LEVEL_GRID_TEMPLATE_COLUMNS, AIFI_LEVELS, AIFI_NEGATIVE_COLOR, getLevelMigrationDescription, pieChartAccentFill, type AifiLevel } from '@/domains/pages/ai-proficiency/constants';
import { AIFI_CHART_TICK_FONT, AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import type { LevelMigrationSummary } from '@/domains/pages/ai-proficiency/fixtures/migration';
import { ringHoverPoint } from '@/domains/pages/ai-proficiency/utils/chart-hover-points';
import { themeColors } from '@/theme/palette';
import { formatUserCount } from '@/domains/pages/ai-proficiency/utils/format-users';
import { cn } from '@/utils/shad';
import { Chart as ChartJS, type ChartOptions, Filler, LineController, LineElement, PointElement, LinearScale, CategoryScale } from 'chart.js';
import { Info, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { useMemo, useRef, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { ChartNoDataEmptyState } from '@/comps/charts/analytics-chart/ChartNoDataEmptyState';
ChartJS.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, chartIndexCrosshairPlugin);
const HIDDEN_DATALABELS = {
  datalabels: {
    display: false
  }
};
type LevelMixChartProps = {
  labels: string[];
  levelMix: Record<string, number[]>;
  levelCounts: Record<string, number[]>;
  migration: LevelMigrationSummary;
  timeRangeLabel?: string;
};

/** Stacked from bottom (Novice) to top (Expert) — matches ladder proficiency order. */
const STACK_ORDER: AifiLevel[] = ['Novice', 'Beginner', 'Practitioner', 'Expert'];
const LEVEL_MIX_TOOLTIP_ID = 'ai-proficiency-level-mix-tooltip';

/** Left-hand movement cards, ordered up → back → flat. */
const MIGRATION_CARDS = [{
  key: 'movedUp',
  label: 'Moved up',
  icon: TrendingUp,
  color: AIFI_LEVEL_COLORS.Expert
}, {
  key: 'slippedBack',
  label: 'Slipped back',
  icon: TrendingDown,
  color: AIFI_NEGATIVE_COLOR
}, {
  key: 'stayed',
  label: 'Stayed',
  icon: Minus,
  color: undefined
}] as const satisfies readonly {
  key: keyof LevelMigrationSummary;
  label: string;
  icon: typeof TrendingUp;
  color: string | undefined;
}[];
export function LevelMixChart(t0) {
  const $ = _c(29);
  const {
    labels,
    levelMix,
    levelCounts,
    migration,
    timeRangeLabel: t1
  } = t0;
  const timeRangeLabel = t1 === undefined ? "30D" : t1;
  const chartRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  useEffect(_temp2, t2);
  const hasData = labels.length > 0;
  let t3;
  if ($[1] !== levelMix) {
    t3 = STACK_ORDER.map((level, index) => {
      const color = AIFI_LEVEL_COLORS[level];
      return {
        label: level,
        data: levelMix[level] ?? [],
        borderColor: color,
        backgroundColor: pieChartAccentFill(color),
        fill: index === 0 ? true : "-1",
        stack: "levels",
        borderWidth: 1,
        tension: 0.35,
        ...ringHoverPoint(color)
      };
    });
    $[1] = levelMix;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const datasets = t3;
  let t4;
  if ($[3] !== datasets || $[4] !== labels) {
    t4 = {
      labels,
      datasets
    };
    $[3] = datasets;
    $[4] = labels;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const chartData = t4;
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      padding: {
        top: 4,
        right: 8,
        left: 0,
        bottom: 0
      }
    };
    t6 = {
      grid: {
        display: false
      },
      border: {
        display: false
      },
      ticks: {
        color: themeColors.text.secondary,
        font: AIFI_CHART_TICK_FONT,
        maxRotation: 0,
        autoSkip: true,
        maxTicksLimit: 12,
        align: "inner"
      }
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      x: t6,
      y: {
        stacked: true,
        min: 0,
        max: 100,
        display: false,
        grid: {
          display: false
        }
      }
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      display: false
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== labels || $[11] !== levelCounts || $[12] !== levelMix) {
    t9 = {
      ...AIFI_LINE_CHART_OPTIONS,
      responsive: true,
      maintainAspectRatio: false,
      interaction: TIME_SERIES_INTERACTION,
      hover: TIME_SERIES_INTERACTION,
      layout: t5,
      scales: t7,
      plugins: {
        ...HIDDEN_DATALABELS,
        legend: t8,
        tooltip: {
          enabled: false,
          external(context) {
            let tooltipEl = document.getElementById(LEVEL_MIX_TOOLTIP_ID);
            if (!tooltipEl) {
              tooltipEl = document.createElement("div");
              tooltipEl.id = LEVEL_MIX_TOOLTIP_ID;
            }
            const tooltipModel = context.tooltip;
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = "0";
              return;
            }
            const index_0 = tooltipModel.dataPoints?.[0]?.dataIndex ?? 0;
            const title = labels[index_0] ?? "";
            const rows = AIFI_LEVELS.map(level_0 => ({
              level: level_0,
              percent: levelMix[level_0]?.[index_0] ?? 0,
              count: levelCounts[level_0]?.[index_0] ?? 0
            }));
            tooltipEl.innerHTML = buildLevelMixTooltipHtml(title, rows);
            positionChartExternalTooltip(context.chart, tooltipEl, tooltipModel);
          }
        }
      }
    };
    $[10] = labels;
    $[11] = levelCounts;
    $[12] = levelMix;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const options = t9;
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      gridTemplateColumns: AIFI_LEVEL_GRID_TEMPLATE_COLUMNS
    };
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== migration || $[16] !== timeRangeLabel) {
    t11 = MIGRATION_CARDS.map(t12 => {
      const {
        key,
        label,
        icon: Icon,
        color: color_0
      } = t12;
      return <div key={key} className="flex min-w-0 flex-1 flex-col justify-center border-b border-b-lightborder py-4 pr-6 pl-8 last:border-b-0" data-testid={`level-migration-card-${key}`}><div className="flex items-baseline gap-2"><span className={cn("text-[32px] leading-none font-medium tracking-tight", AIFI_DATA_NUMBER_CLASS)} style={color_0 ? {
            color: color_0
          } : undefined}>{formatUserCount(migration[key])}</span><span className={cn("text-xs text-muted-foreground", AIFI_DATA_NUMBER_CLASS)}>{timeRangeLabel}</span></div><h3 className="mt-2.5 flex min-w-0 items-center gap-1.5 text-xs leading-snug text-muted-foreground"><Icon className="size-3.5 shrink-0" style={{
            color: color_0 ?? themeColors.text.secondary
          }} /><span className="line-clamp-1">{label}</span><BasicTooltip text={getLevelMigrationDescription(key, timeRangeLabel)} side="bottom" classNameContent="max-w-80 break-normal leading-relaxed"><button type="button" aria-label={`About ${label}`} className="shrink-0 cursor-default text-muted-foreground outline-hidden transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"><Info className="size-3.5" aria-hidden={true} /></button></BasicTooltip></h3></div>;
    });
    $[15] = migration;
    $[16] = timeRangeLabel;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11) {
    t12 = <div className="flex min-w-0 flex-col border-r border-lightborder">{t11}</div>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      gridColumn: `span ${AIFI_LEVEL_CHART_COLUMN_SPAN}`
    };
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">{AIFI_LEVELS.map(_temp3)}</div>;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== chartData || $[23] !== hasData || $[24] !== options) {
    t15 = <div className="flex min-w-0 flex-col gap-3 py-4 pr-8 pl-6" style={t13}>{t14}<div className="relative mt-1 h-55 w-full overflow-hidden">{hasData ? <Line ref={chartRef} data={chartData} options={options} /> : <ChartNoDataEmptyState />}</div></div>;
    $[22] = chartData;
    $[23] = hasData;
    $[24] = options;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t12 || $[27] !== t15) {
    t16 = <div className="grid items-stretch" style={t10} data-testid="level-mix-chart">{t12}{t15}</div>;
    $[26] = t12;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  return t16;
}
function _temp3(level_1) {
  return <BasicTooltip key={level_1} text={AIFI_LEVEL_DESCRIPTIONS[level_1]} side="bottom" classNameContent="max-w-80 break-normal leading-relaxed"><span className="flex items-center gap-2"><span className="size-2 rounded-full" style={{
        backgroundColor: AIFI_LEVEL_COLORS[level_1]
      }} />{level_1}</span></BasicTooltip>;
}
function _temp2() {
  return _temp;
}
function _temp() {
  document.getElementById(LEVEL_MIX_TOOLTIP_ID)?.remove();
}